import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@amzn/awsui-components-react/polaris/icon";
import SideNavigation, {
  SideNavigationProps,
} from "@amzn/awsui-components-react/polaris/side-navigation";
import { Stage } from "../../lusca-search-api/generated-src";
import {
  DEFAULT_GROUPS_QUERY,
  useInvestigationList,
  useInvestigationSuggestions,
  useUninvestigatedGroups,
} from "../../hooks/useInvestigations";
import { GroupsFilterQuery } from "./groupsFilter";
import useCurrentUser from "../../hooks/useCurrentUser";
import { InvestigationSection } from "./InvestigationOverview";
import {
  FilterPreset,
  REGISTRY_PRESETS,
  backlogPresets,
  matchingPreset,
  sectionPath,
} from "./investigationUtils";
import { monitoringClear } from "./readyToResolve";

interface InvestigationsSideNavProps {
  stage: Stage;
  /** The section the current page shows; none on an investigation's own page. */
  active?: InvestigationSection;
  /** The table filter in force on the active section, if any; picks the preset to highlight. */
  filter?: GroupsFilterQuery;
}

const count = (n: number | undefined, more = false) =>
  n === undefined ? undefined : `${n}${more ? "+" : ""}`;

type NavCounts = Record<string, string | undefined>;

export const DLQ_INVESTIGATIONS_WIKI_URL =
  "https://w.amazon.com/bin/view/AIV/FinSys/TRACK/Systems/DLQInvestigations";

const COUNTS_STORAGE_PREFIX = "lusca.investigationsNav.counts.";

const readCounts = (key: string): NavCounts => {
  try {
    const raw = window.localStorage.getItem(COUNTS_STORAGE_PREFIX + key);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? (parsed as NavCounts) : {};
  } catch {
    return {};
  }
};

/**
 * Each badge shows its live count once loaded and, until then, the count last seen on this
 * browser (per stage and user), so a page load doesn't flash blank/0 and then jump. Only loaded
 * values are ever stored.
 */
function useLastKnownCounts(key: string, live: NavCounts): NavCounts {
  const cached = React.useMemo(() => readCounts(key), [key]);
  const merged: NavCounts = { ...cached };
  Object.entries(live).forEach(([k, v]) => {
    if (v !== undefined) merged[k] = v;
  });
  const serialized = JSON.stringify(merged);
  React.useEffect(() => {
    try {
      window.localStorage.setItem(COUNTS_STORAGE_PREFIX + key, serialized);
    } catch {
      // Storage full or disabled: the nav just falls back to live counts.
    }
  }, [key, serialized]);
  return merged;
}

/**
 * The investigations area's navigation: an overview, the registry slices, the two feeds of
 * unowned work, and the lookup tool. Counts come from queries every page already runs, so the
 * nav costs nothing extra. Rendered on the section pages and on an investigation's own page.
 */
export const InvestigationsSideNav: React.FC<InvestigationsSideNavProps> = ({
  stage,
  active,
  filter,
}) => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const activeList = useInvestigationList(stage, { view: "active" });
  const backlogSlices = backlogPresets();
  // One backlog call answers both the row total and every slice badge (countFilters), counted in
  // the category view the backlog table shows so the badges agree with its counter.
  const groups = useUninvestigatedGroups(stage, false, {
    ...DEFAULT_GROUPS_QUERY,
    countFilters: backlogSlices.map((p) => p.filter),
    byCategory: true,
  });
  const suggestions = useInvestigationSuggestions(stage);

  const activeItems = activeList.items;
  // Until the first page arrives the list is empty, not zero, so live counts are undefined until
  // loaded and the last-known counts (below) stand in rather than a flash of "0".
  const activeLoaded = activeList.data !== undefined;
  const more = !!activeList.hasMore;
  const activeCount = (pick: (inv: (typeof activeItems)[number]) => boolean) =>
    activeLoaded ? count(activeItems.filter(pick).length, more) : undefined;
  const live: NavCounts = {
    active: activeLoaded ? count(activeItems.length, more) : undefined,
    mine: currentUser
      ? activeCount((inv) => inv.owner === currentUser)
      : undefined,
    unowned: activeCount((inv) => !inv.owner),
    ready: activeCount((inv) => monitoringClear(inv)),
    stale: activeCount((inv) => !!inv.isStale),
    following: currentUser
      ? activeCount((inv) => !!inv.followers?.includes(currentUser))
      : undefined,
    // Totals, like every other count in the nav; the page itself says how many are unowned.
    patterns: suggestions.data?.available
      ? count(suggestions.data.suggestions.length)
      : undefined,
    backlog: groups.data?.available
      ? count(groups.data.totalRows ?? groups.data.totalGroups)
      : undefined,
  };
  if (groups.data?.available) {
    backlogSlices.forEach((preset, i) => {
      live[`backlog:${preset.id}`] = count(groups.data?.filterCounts?.[i]);
    });
  }
  const shown = useLastKnownCounts(`${stage}:${currentUser ?? ""}`, live);
  const backlogSliceCounts: Partial<Record<string, string | undefined>> = {};
  backlogSlices.forEach((preset) => {
    backlogSliceCounts[preset.id] = shown[`backlog:${preset.id}`];
  });

  // "Ready to resolve" and "Stale" only earn a line when non-empty (or they are the page shown).
  const registryPresets = REGISTRY_PRESETS.filter(
    (p) =>
      (p.id !== "ready" && p.id !== "stale" && p.id !== "following") ||
      (!!shown[p.id] && shown[p.id] !== "0") ||
      (active === "active" && matchingPreset([p], filter))
  );

  const info = (text?: string) =>
    text ? <span aria-label={`${text} items`}>{text}</span> : undefined;

  const link = (
    section: InvestigationSection,
    text: string,
    total?: string
  ): SideNavigationProps.Link => ({
    type: "link",
    text,
    href: sectionPath(section, stage),
    info: info(total),
  });

  // A filterable table is a link group: the plain link plus one-click slices. A preset is just the
  // same page with `filter=` in the URL, so the table's filter bar shows what was applied and any
  // further token the user adds stays in place when switching between slices.
  const group = (
    section: InvestigationSection,
    text: string,
    presets: readonly FilterPreset[],
    total?: string,
    presetCounts: Partial<Record<string, string | undefined>> = {}
  ): SideNavigationProps.LinkGroup => ({
    type: "link-group",
    text,
    href: sectionPath(section, stage),
    info: info(total),
    items: presets.map((preset) => ({
      type: "link",
      text: preset.text,
      href: sectionPath(section, stage, preset.filter),
      info: info(presetCounts[preset.id]),
    })),
  });

  // Highlight the preset whose filter is in force; any other filter keeps the group's own link lit.
  const presetsFor = (
    section: InvestigationSection | undefined
  ): readonly FilterPreset[] => {
    if (section === "active") return REGISTRY_PRESETS;
    if (section === "backlog") return backlogSlices;
    return [];
  };
  const activePreset = matchingPreset(presetsFor(active), filter);
  let activeHref: string | undefined;
  if (active && activePreset) {
    activeHref = sectionPath(active, stage, activePreset.filter);
  } else if (active === "cancelled") {
    activeHref = sectionPath("resolved", stage);
  } else if (active) {
    activeHref = sectionPath(active, stage);
  }

  const items: SideNavigationProps.Item[] = [
    link("overview", "Overview"),
    {
      type: "section",
      text: "Work",
      items: [
        group(
          "active",
          "Active investigations",
          registryPresets,
          shown.active,
          {
            mine: shown.mine,
            unowned: shown.unowned,
            following: shown.following,
            ready: shown.ready,
            stale: shown.stale,
          }
        ),
        // Cancelled cases are reached from the Resolved page's switcher, not the nav.
        link("resolved", "Resolved"),
        // Every case whatever its status, e.g. to see everything one person has owned.
        link("all", "All investigations"),
        link("activity", "Activity"),
      ],
    },
    {
      type: "section",
      text: "Find work",
      items: [
        // Short so it fits the narrow laptop nav on one line; the page title keeps the full name.
        link("suggestions", "TrackStars", shown.patterns),
        group(
          "backlog",
          "Backlog",
          backlogSlices,
          shown.backlog,
          backlogSliceCounts
        ),
      ],
    },
    { type: "divider" },
    { ...link("lookup", "Look up a DLQ item"), icon: <Icon name="search" /> },
    {
      type: "link",
      text: "Wiki",
      href: DLQ_INVESTIGATIONS_WIKI_URL,
      external: true,
    },
  ];

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{
        text: "Investigations",
        href: sectionPath("overview", stage),
      }}
      items={items}
      onFollow={(event) => {
        if (event.detail.external) return;
        event.preventDefault();
        navigate(event.detail.href);
      }}
    />
  );
};

export default InvestigationsSideNav;
